import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    getProcessHistoryOptions, getProcessHistoryStats, listProcessHistory,
    ProcessHistoryOptions, ProcessHistoryRow, ProcessHistoryStats,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import { thumbUrl, THUMB_W } from '../../utils/imgUrl';
import Pagination from '../../components/Pagination';
import { toast } from '../../utils/toast';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { BarList, MaterialSourceBadge, NoticeBar, ProcessThumb, DislikeRateCell, SegGroup, SortDirToggle, SortTimeHeader, StatCard } from './parts';
import { EVAL_OPTION_LABEL, EvaluateBadge, RestoredBadge, fmtOccurred, restoreText } from './processHistoryLabels';
import SceneCard from './SceneCard';
import { useMModeText } from '../../utils/mmodeText';
import SearchSelect from '../../components/SearchSelect';
import { sortByDir, SortDir } from '../../utils/sortDesc';

// 数据资产·历史加工数据 (2026-09-07, doc/数据资产/历史加工数据-实施方案.md):
// xcs2.7 加工反馈 2025-08~11 共 104,161 条一次性导入, 结果图已搬到本平台存储.
// 与「加工数据中心」(Studio 1.9 实时) 并列不混算: 这份没有加工前图、没有推荐参数, 但有 10 万级样本.
// 页面: 统计 / 明细 (图墙 | 表格) 双视图, 详情为独立路由页.

const PAGE_SIZE = 24;

const pct = (n: number, d: number) => (d > 0 ? `${((n / d) * 100).toFixed(1)}%` : '—');

function ProcessHistoryList() {
    // 加工模式名从 mm_mode 字典取 (SSOT), 不在页面里维护常量表.
    const modeText = useMModeText();
    const history = useHistory();
    const initQ = new URLSearchParams(useLocation().search);
    const [tab, setTab] = useState(initQ.get('tab') === 'table' ? 'table' : 'stats');
    const [elemDir, setElemDir] = useState<SortDir>('desc');
    // 默认表格 —— 与「加工数据中心」同款的横向列表 (一行一条, 加工前/后各占一列);
    // 图墙保留成可选视图, 想快速扫图时再切。
    const [view, setView] = useState<'grid' | 'table'>(initQ.get('view') === 'grid' ? 'grid' : 'table');
    const [device, setDevice] = useState(initQ.get('device') || '');
    const [mode, setMode] = useState(initQ.get('mode') || '');
    const [materialSource, setMaterialSource] = useState(initQ.get('materialSource') || '');
    const [materialXcode, setMaterialXcode] = useState(initQ.get('materialXcode') || '');
    const [evaluate, setEvaluate] = useState(initQ.get('evaluate') || '');
    const [elementType, setElementType] = useState(initQ.get('elementType') || '');
    const [hasImage, setHasImage] = useState(initQ.get('hasImage') || '');
    // 耗材还原 (2026-09-20): restored 是主筛 (走父表索引); qc/finish/label 是还原子集内的细筛.
    const [restored, setRestored] = useState(initQ.get('restored') || '');
    const [qcVerdict, setQcVerdict] = useState(initQ.get('qcVerdict') || '');
    const [restoreFinish, setRestoreFinish] = useState(initQ.get('restoreFinish') || '');
    const [labelCat, setLabelCat] = useState(initQ.get('labelCat') || '');
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [keywordDraft, setKeywordDraft] = useState(initQ.get('keyword') || '');
    const [uid, setUid] = useState(initQ.get('uid') || '');
    const [uidDraft, setUidDraft] = useState(initQ.get('uid') || '');
    const [dateFrom, setDateFrom] = useState(initQ.get('dateFrom') || '');
    const [dateTo, setDateTo] = useState(initQ.get('dateTo') || '');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    // 明细按发生时间排序: 默认新→旧, 表头「时间」可切旧→新.
    const [order, setOrder] = useState<'asc' | 'desc'>(initQ.get('order') === 'asc' ? 'asc' : 'desc');
    const toggleOrder = () => { setOrder((o) => (o === 'asc' ? 'desc' : 'asc')); setPage(1); };

    const [list, setList] = useState<ProcessHistoryRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [stats, setStats] = useState<ProcessHistoryStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [options, setOptions] = useState<ProcessHistoryOptions | null>(null);
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useSyncQueryToUrl('/assets/process-history', {
        tab: tab === 'stats' ? '' : tab, view: view === 'table' ? '' : view,
        device, mode, materialSource, materialXcode, evaluate, elementType, hasImage, keyword, uid, dateFrom, dateTo, page,
        restored, qcVerdict, restoreFinish, labelCat, order: order === 'asc' ? 'asc' : '',
    });

    const filterParams = useMemo(() => ({
        device: device || undefined, mode: mode || undefined, materialSource: materialSource || undefined,
        materialXcode: materialXcode || undefined, evaluate: evaluate || undefined, elementType: elementType || undefined,
        hasImage: hasImage || undefined, keyword: keyword || undefined, uid: uid || undefined,
        restored: restored || undefined, qcVerdict: qcVerdict || undefined,
        restoreFinish: restoreFinish || undefined, labelCat: labelCat || undefined,
        dateFrom: dateFrom || undefined, dateTo: dateTo || undefined,
    }), [device, mode, materialSource, materialXcode, evaluate, elementType, hasImage, keyword, uid, dateFrom, dateTo,
        restored, qcVerdict, restoreFinish, labelCat]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listProcessHistory({ ...filterParams, order: order === 'asc' ? 'asc' : undefined, page, pageSize: PAGE_SIZE });
            setList(res.list || []);
            setTotal(res.total);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
            setList([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [filterParams, page, order]);

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            setStats(await getProcessHistoryStats(filterParams));
        } catch (e) {
            toast(e instanceof ApiError ? e.message : String(e));
        } finally {
            setStatsLoading(false);
        }
    }, [filterParams]);

    useEffect(() => { if (tab === 'table') load(); }, [load, tab]);
    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => { getProcessHistoryOptions().then(setOptions).catch(() => undefined); }, []);

    const resetFilters = () => {
        setDevice(''); setMode(''); setMaterialSource(''); setMaterialXcode(''); setEvaluate(''); setElementType('');
        setHasImage(''); setKeyword(''); setKeywordDraft(''); setUid(''); setUidDraft(''); setDateFrom(''); setDateTo('');
        setRestored(''); setQcVerdict(''); setRestoreFinish(''); setLabelCat(''); setPage(1);
    };
    // 下钻: 统计图点一下 → 带条件切到明细.
    const drill = (patch: Partial<{
        device: string; mode: string; materialSource: string; materialXcode: string; evaluate: string; elementType: string;
        restored: string; qcVerdict: string; restoreFinish: string;
    }>) => {
        if (patch.device !== undefined) setDevice(patch.device);
        if (patch.mode !== undefined) setMode(patch.mode);
        if (patch.materialSource !== undefined) setMaterialSource(patch.materialSource);
        if (patch.materialXcode !== undefined) setMaterialXcode(patch.materialXcode);
        if (patch.evaluate !== undefined) setEvaluate(patch.evaluate);
        if (patch.elementType !== undefined) setElementType(patch.elementType);
        if (patch.restored !== undefined) setRestored(patch.restored);
        if (patch.qcVerdict !== undefined) setQcVerdict(patch.qcVerdict);
        if (patch.restoreFinish !== undefined) setRestoreFinish(patch.restoreFinish);
        setPage(1);
        setTab('table');
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const k = stats?.kpi;
    const filtered = Boolean(device || mode || materialSource || materialXcode || evaluate || elementType || hasImage || keyword || uid || dateFrom || dateTo
        || restored || qcVerdict || restoreFinish || labelCat);
    const dayMax = Math.max(1, ...(stats?.byDay || []).map((d) => d.count));

    return (
        <div className="space-y-5">
            {/* 页头 */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] font-extrabold tracking-[4px] uppercase text-slate-400 mb-1">XCS 2.7 · Process Evaluate</div>
                    <div className="text-xl font-extrabold text-slate-900 tracking-tight">历史加工数据</div>
                    <div className="text-xs text-slate-500 mt-1.5">
                        Studio 加工结束上报的历史快照（2025-08 ～ 2025-11，一次性导入）—— 加工了什么、用了什么参数、结果图长什么样。
                        <span className="text-slate-400"> 与「材料加工」(Studio 1.9 实时) 并列，不混算。</span>
                    </div>
                </div>
                <div className="text-right text-[11px] text-slate-400 leading-relaxed shrink-0">
                    <div className="text-slate-500 font-semibold">数据快照 · 不再增长</div>
                    <div>结果图存本平台存储 · 材料按主数据关联</div>
                </div>
            </div>

            {/* 筛选 (统计与明细共用) */}
            <div className="card px-4 py-3 space-y-2.5">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <SegGroup label="材料来源" value={materialSource} onChange={(v) => { setMaterialSource(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'official', label: '官方材料' }, { value: 'user_custom', label: '用户自定义' }, { value: 'unknown', label: '无标识' }]} />
                    <SegGroup label="反馈" value={evaluate} onChange={(v) => { setEvaluate(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'has', label: '有反馈' }, { value: 'like', label: '满意' }, { value: 'dislike', label: '不满意' }]} />
                    <SegGroup label="结果图" value={hasImage} onChange={(v) => { setHasImage(v); setPage(1); }}
                        options={[{ value: '', label: '全部' }, { value: 'yes', label: '有图' }, { value: 'no', label: '无图' }]} />
                    {/* 已还原 / 仅原始: 同一批数据的两种形态 —— 已还原的那些多一张模型反推的加工前图 */}
                    <SegGroup label="耗材还原" value={restored} onChange={(v) => {
                        setRestored(v);
                        if (v === 'no') { setQcVerdict(''); setRestoreFinish(''); setLabelCat(''); } // 仅原始时细筛无意义, 一并清掉
                        setPage(1);
                    }}
                        options={[{ value: '', label: '全部' }, { value: 'yes', label: '已还原' }, { value: 'no', label: '仅原始' }]} />
                    <button type="button" className="ml-auto text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded" onClick={resetFilters}>清空</button>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2.5 border-t border-slate-50">
                    <SearchSelect className="w-40" value={device} onChange={(v) => { setDevice(v); setPage(1); }} placeholder="全部设备" clearable
                        options={(options?.devices || []).map((d) => ({ value: d.key, label: `${d.name || d.key} · ${d.count.toLocaleString()}` }))} />
                    <SearchSelect className="w-40" value={mode} onChange={(v) => { setMode(v); setPage(1); }} placeholder="全部加工模式" clearable
                        options={(options?.modes || []).map((m) => ({ value: m.key, label: `${modeText(m.key)} · ${m.count.toLocaleString()}` }))} />
                    <SearchSelect className="w-36" value={elementType} onChange={(v) => { setElementType(v); setPage(1); }} placeholder="全部元素类型" clearable
                        options={(options?.elementTypes || []).map((t) => ({ value: t.key, label: `${t.key} · ${t.count.toLocaleString()}` }))} />
                    <input className="inp" style={{ width: 160 }} placeholder="材料名关键词…" value={keywordDraft}
                        onChange={(e) => setKeywordDraft(e.target.value)}
                        onBlur={() => { if (keywordDraft.trim() !== keyword) { setKeyword(keywordDraft.trim()); setPage(1); } }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setKeyword(keywordDraft.trim()); setPage(1); } }} />
                    <input className="inp" style={{ width: 120 }} placeholder="用户 UID…" value={uidDraft}
                        onChange={(e) => setUidDraft(e.target.value)}
                        onBlur={() => { if (uidDraft.trim() !== uid) { setUid(uidDraft.trim()); setPage(1); } }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setUid(uidDraft.trim()); setPage(1); } }} />
                    <input type="date" className="inp" style={{ width: 148 }} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
                    <span className="text-slate-300">~</span>
                    <input type="date" className="inp" style={{ width: 148 }} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
                    {restored !== 'no' && (
                        <>
                            <SearchSelect className="w-[130px]" value={qcVerdict} onChange={(v) => { setQcVerdict(v); setPage(1); }} placeholder="还原质检" clearable
                                options={(options?.qcVerdicts || []).map((v) => ({ value: v.key, label: `${restoreText('qc_verdict', v.key)} · ${v.count.toLocaleString()}` }))} />
                            <SearchSelect className="w-[130px]" value={restoreFinish} onChange={(v) => { setRestoreFinish(v); setPage(1); }} placeholder="表面判定" clearable
                                options={(options?.restoreFinishes || []).map((v) => ({ value: v.key, label: `${restoreText('restore_finish', v.key)} · ${v.count.toLocaleString()}` }))} />
                            <SearchSelect className="w-[130px]" value={labelCat} onChange={(v) => { setLabelCat(v); setPage(1); }} placeholder="人工标注" clearable
                                options={(options?.labelCats || []).map((v) => ({ value: v.key, label: `${restoreText('restore_label', v.key)} · ${v.count.toLocaleString()}` }))} />
                        </>
                    )}
                    {materialXcode && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-50 text-indigo-600 text-xs">
                            材料 <b className="font-mono">{materialXcode}</b>
                            <button type="button" className="ml-1 text-indigo-400 hover:text-indigo-700" onClick={() => { setMaterialXcode(''); setPage(1); }}>×</button>
                        </span>
                    )}
                </div>
            </div>

            {/* tabs */}
            <div className="flex items-center gap-1">
                {[['stats', '统计'], ['table', '明细']].map(([key, label]) => (
                    <button key={key} type="button"
                        className={`px-[15px] py-1.5 rounded-[9px] text-[13px] font-semibold ${tab === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        onClick={() => setTab(key)}>
                        {label}
                    </button>
                ))}
                {tab === 'table' && (
                    <div className="ml-auto flex items-center gap-1 text-xs">
                        {([['table', '表格'], ['grid', '图墙']] as const).map(([key, label]) => (
                            <button key={key} type="button"
                                className={`px-2.5 py-1 rounded-md ${view === key ? 'bg-slate-200 text-slate-800 font-semibold' : 'text-slate-500 hover:bg-slate-100'}`}
                                onClick={() => setView(key)}>{label}</button>
                        ))}
                    </div>
                )}
            </div>

            {tab === 'stats' ? (
                <div className="space-y-4">
                    <NoticeBar>
                        满意度反馈<b>极稀疏</b>：104,161 条里只有 <b>562</b> 条带反馈（满意 517 / 不满意 45），且 540 条集中在 2025-11-14 —— 反馈采集是数据截止前最后一天才上线的。下面凡涉及「满意/不满意」的图，分母都是<b>有反馈子集</b>，不是全量。
                        {filtered && <span className="ml-1 text-indigo-500">当前统计已随筛选收敛。</span>}
                    </NoticeBar>

                    {/* KPI */}
                    <div className="grid grid-cols-5 gap-4">
                        <StatCard label="加工记录" value={k ? k.total.toLocaleString() : '—'} sub={k ? `${k.users.toLocaleString()} 个用户 · ${k.devices} 种设备` : ''} />
                        <StatCard label="有结果图" value={k ? k.withImage.toLocaleString() : '—'} sub={k ? `占 ${pct(k.withImage, k.total)}` : ''} accent="text-indigo-600" />
                        <StatCard label="有反馈" value={k ? k.withFeedback.toLocaleString() : '—'}
                            sub={k ? <span><span className="text-emerald-600">满意 {k.like}</span> · <span className="text-rose-500">不满意 {k.dislike}</span></span> : ''} />
                        <StatCard label="官方材料" value={k ? pct(k.official, k.total) : '—'} sub={k ? `${k.official.toLocaleString()} 条 · 关联到 ${k.materials} 种平台材料` : ''} accent="text-emerald-600" />
                        <StatCard label="用户自定义材料" value={k ? pct(k.userCustom, k.total) : '—'} sub={k ? `${k.userCustom.toLocaleString()} 条 · 无标识 ${k.unknown.toLocaleString()}` : ''} accent="text-amber-600" />
                        {/* 分母是"有结果图"而不是总数: 没有结果图的记录根本无从还原, 用总数算会把覆盖率说低 */}
                        <StatCard label="已还原" value={k ? k.restored.toLocaleString() : '—'}
                            sub={k ? `占有图的 ${pct(k.restored, k.withImage)} · 多一张加工前图` : ''} accent="text-violet-600" />
                    </div>

                    {/* 按天趋势 */}
                    <div className="card p-4">
                        <div className="flex items-baseline gap-2 mb-1">
                            <div className="text-sm font-semibold text-slate-700">按天上报量</div>
                            <div className="text-[11px] text-slate-400 ml-auto">{stats?.byDay.length || 0} 天 · 深色 = 有结果图</div>
                        </div>
                        {statsLoading && !stats ? <div className="text-xs text-slate-400 py-6 text-center">统计中…</div> : (
                            <div className="flex items-end gap-[2px] mt-2" style={{ height: 120 }} title="悬停看每日数字">
                                {(stats?.byDay || []).map((d) => (
                                    <div key={d.day} className="flex-1 group relative" style={{ minWidth: 2, height: `${Math.max(2, (d.count / dayMax) * 120)}px` }}>
                                        <div className="absolute inset-0 rounded-t-sm bg-indigo-100" />
                                        <div className="absolute bottom-0 left-0 right-0 bg-indigo-500 rounded-t-sm"
                                            style={{ height: `${d.count > 0 ? (d.withImage / d.count) * 100 : 0}%` }} />
                                        <div className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] bg-slate-800 text-white px-1.5 py-0.5 rounded z-10">
                                            {d.day.slice(0, 4)}-{d.day.slice(4, 6)}-{d.day.slice(6, 8)} · {d.count} 条 · 有图 {d.withImage}{d.feedback ? ` · 反馈 ${d.feedback}` : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {stats && stats.byDay.length > 0 && (
                            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                                <span>{stats.byDay[0].day}</span><span>{stats.byDay[stats.byDay.length - 1].day}</span>
                            </div>
                        )}
                    </div>

                    {/* 设备 | 模式 */}
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="设备分布" hint="按本平台设备族归一 · 点击下钻" labelWidth="w-32"
                            rows={(stats?.byDevice || []).map((d) => ({ label: d.name || d.key, value: d.count, share: pct(d.count, k?.total || 0), onClick: () => drill({ device: d.key }) }))} />
                        <BarList title="加工模式" hint="processingData.mode · 点击下钻" labelWidth="w-32"
                            rows={(stats?.byMode || []).map((m) => ({ label: modeText(m.key), value: m.count, share: pct(m.count, k?.total || 0), onClick: () => drill({ mode: m.key }) }))} />
                    </div>

                    {/* 加工场景识别 */}
                    <SceneCard scene={stats?.scene} />

                    {/* 元素死亡排行 | 材料 Top */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="card p-4">
                            <div className="flex items-baseline gap-2 mb-1">
                                <div className="text-sm font-semibold text-slate-700">元素类型 × 不满意率</div>
                                <div className="text-[11px] text-slate-400 ml-auto">「死亡元素」排行 · 仅有反馈子集</div>
                                <SortDirToggle value={elemDir} onChange={setElemDir} />
                            </div>
                            <div className="text-[11px] text-slate-400 mb-3">哪种元素最容易导致差评。分母 = 该类型元素里带反馈的条数，样本很小，看趋势别看小数。</div>
                            <table className="w-full text-xs">
                                <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                                    <th className="py-1.5">类型</th><th className="py-1.5 text-right">元素数</th><th className="py-1.5 text-right">满意</th><th className="py-1.5 text-right">不满意</th><th className="py-1.5 text-right pr-2">不满意率</th>
                                </tr></thead>
                                <tbody>
                                    {sortByDir(stats?.byElementType || [], (e) => e.total, elemDir).map((e) => {
                                        return (
                                            <tr key={e.elementType} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => drill({ elementType: e.elementType })}>
                                                <td className="py-1.5 font-medium text-slate-700">{e.elementType}</td>
                                                <td className="py-1.5 text-right font-mono text-slate-600">{e.total.toLocaleString()}</td>
                                                <td className="py-1.5 text-right font-mono text-emerald-600">{e.like}</td>
                                                <td className="py-1.5 text-right font-mono text-rose-500">{e.dislike}</td>
                                                <td className="py-1.5 text-right pr-2"><DislikeRateCell like={e.like} dislike={e.dislike} /></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <BarList title="加工最多的材料 Top" hint="按关联到的平台材料聚合 · 点击下钻" labelWidth="w-44" maxRows={12}
                            rows={(stats?.topMaterials || []).map((m) => ({ label: m.name || m.key, value: m.count, share: pct(m.count, k?.official || 0), onClick: () => drill({ materialXcode: m.key }) }))}
                            footer={<div className="text-[11px] text-slate-400">只统计能关联到本平台材料主数据的记录（官方材料）；用户自定义材料没有编码，不进榜。</div>} />
                    </div>

                    {/* 材料来源 | 差评原因 */}
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="材料来源" hint="源材料 id ↔ 本平台旧库 ID 关联结果" labelWidth="w-32"
                            rows={(stats?.byMaterialSource || []).map((s) => ({
                                label: s.key === 'official' ? '官方材料' : s.key === 'user_custom' ? '用户自定义' : '无标识',
                                value: s.count, share: pct(s.count, k?.total || 0),
                                color: s.key === 'official' ? '#10b981' : s.key === 'user_custom' ? '#f59e0b' : '#cbd5e1',
                                onClick: () => drill({ materialSource: s.key }),
                            }))} />
                        <BarList title="不满意原因" hint={`结构化差评原因 · 共 ${k?.dislike ?? 0} 条不满意`} labelWidth="w-32"
                            rows={(stats?.dislikeReasons || []).map((r) => ({ label: EVAL_OPTION_LABEL[r.key] || r.key, value: r.count, color: '#f43f5e', onClick: () => drill({ evaluate: 'dislike' }) }))}
                            footer={(stats?.dislikeReasons?.length ?? 0) === 0 ? <div className="text-[11px] text-slate-400">当前筛选下没有带原因码的差评。</div> : undefined} />
                    </div>

                    {/* 耗材还原: 两块都只统计已还原子集, 分母是 kpi.restored 而不是总数 */}
                    <div className="grid grid-cols-2 gap-4">
                        <BarList title="还原质检" hint="视觉质检结论 · 点击下钻" labelWidth="w-32"
                            rows={(stats?.byQcVerdict || []).map((v) => ({
                                label: restoreText('qc_verdict', v.key), value: v.count, share: pct(v.count, k?.restored || 0),
                                color: v.key === 'ok' ? '#10b981' : v.key === 'minor' ? '#0ea5e9' : '#f59e0b',
                                onClick: () => drill({ restored: 'yes', qcVerdict: v.key }),
                            }))}
                            footer={<div className="text-[11px] text-slate-400">
                                质检是<b>复核优先级</b>，不是判废依据 —— 「待复核」的图仍然可用，只是建议人眼再看一遍。
                            </div>} />
                        <BarList title="还原表面判定" hint="模型对耗材表面的判定 · 点击下钻" labelWidth="w-32"
                            rows={(stats?.byRestoreFinish || []).map((v) => ({
                                label: restoreText('restore_finish', v.key), value: v.count, share: pct(v.count, k?.restored || 0),
                                onClick: () => drill({ restored: 'yes', restoreFinish: v.key }),
                            }))}
                            footer={(stats?.byRestoreFinish?.length ?? 0) === 0
                                ? <div className="text-[11px] text-slate-400">当前筛选下没有已还原的记录。</div> : undefined} />
                    </div>
                </div>
            ) : (
                <>
                    {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}
                    <div className="card">
                        <div className="flex items-center px-4 pt-3 pb-2">
                            <div className="text-sm font-semibold text-slate-700">加工明细</div>
                            <div className="ml-2 text-xs text-slate-400">{filtered ? '已筛选' : '全部记录'} · 按发生时间{order === 'asc' ? '正序' : '倒序'}</div>
                            <div className="ml-auto text-xs text-slate-400">共 {total.toLocaleString()} 条</div>
                        </div>
                        {/* 「加工前」这一列的口径说明: 它来自耗材还原, 不是源数据自带的实拍图 —— 不写明白
                            会被当成"大部分记录缺图", 实际上是"大部分记录还没做还原"。 */}
                        <div className="px-4 pb-2 text-[11px] text-slate-400">
                            「加工前」是<b className="text-violet-600">已还原</b>记录才有的耗材还原图（视觉模型从结果图反推的加工前样子，
                            <b>推测、非实拍</b>）；未还原的记录该列显示「无」。
                            {/* 条数取实时统计, 不写死 —— 还原是会再跑批次的, 写死的数字迟早过期 */}
                            {k ? `当前条件下已还原 ${k.restored.toLocaleString()} 条。` : ''}
                        </div>
                        {loading ? (
                            <div className="text-center text-slate-400 py-12 text-sm">加载中…</div>
                        ) : list.length === 0 ? (
                            <div className="text-center text-slate-400 py-12 text-sm">没有符合条件的记录</div>
                        ) : view === 'grid' ? (
                            <div className="grid gap-3 px-4 pb-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                                {list.map((r) => (
                                    <div key={r.id} className="group rounded-xl border border-slate-100 overflow-hidden hover:shadow-md hover:border-slate-200 transition cursor-pointer bg-white"
                                        onClick={() => history.push(`/assets/process-history/${r.id}`)}>
                                        {/* 已还原的卡片并排摆「加工前 | 加工后」两格, 未还原的仍是整张结果图 ——
                                            前后对比得一眼看见, 藏在 hover 里等于没有. */}
                                        <div className="relative bg-slate-100" style={{ aspectRatio: '4 / 3' }}>
                                            {r.restoredImageUrl ? (
                                                <div className="absolute inset-0 grid grid-cols-2 gap-px bg-white">
                                                    {([
                                                        { src: r.restoredImageUrl, label: '加工前', hint: '加工前 · AI 还原(推测) · 点击看大图' },
                                                        { src: r.imageUrl, label: '加工后', hint: '加工后 · 点击看大图' },
                                                    ]).map((c) => (
                                                        <div key={c.label} className="relative bg-slate-100 overflow-hidden">
                                                            {c.src ? (
                                                                <img src={thumbUrl(c.src, THUMB_W)} alt={c.label} title={c.hint} loading="lazy" decoding="async"
                                                                    className="w-full h-full object-cover cursor-zoom-in"
                                                                    onClick={(e) => { e.stopPropagation(); setZoomSrc(c.src); }} />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-[11px] text-slate-400">无</div>
                                                            )}
                                                            <div className="absolute bottom-1 left-1 px-1 py-0.5 rounded bg-black/55 text-white text-[10px]">{c.label}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : r.imageUrl ? (
                                                <img src={thumbUrl(r.imageUrl, THUMB_W)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"
                                                    onClick={(e) => { e.stopPropagation(); setZoomSrc(r.imageUrl); }} />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-[11px] text-slate-400">无结果图</div>
                                            )}
                                            <div className="absolute top-1.5 left-1.5 flex gap-1">
                                                <EvaluateBadge type={r.evaluateType} compact />
                                            </div>
                                            {r.restoredImageUrl && (
                                                <div className="absolute top-1.5 right-1.5"><RestoredBadge compact /></div>
                                            )}
                                            <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/55 text-white text-[10px] font-mono">{r.deviceName || r.deviceIdRaw}</div>
                                        </div>
                                        <div className="p-2.5">
                                            <div className="text-[13px] font-medium text-slate-800 truncate" title={r.materialName}>{r.materialName || <span className="text-slate-400">未命名材料</span>}</div>
                                            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400">
                                                <MaterialSourceBadge source={r.materialSource} />
                                                <span className="truncate">{modeText(r.mode)}</span>
                                                <span className="ml-auto whitespace-nowrap">{r.elementCount} 元素</span>
                                            </div>
                                            <div className="mt-1 text-[10px] text-slate-400 font-mono">{fmtOccurred(r.occurredAt)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-[13px]" style={{ minWidth: 900 }}>
                                    <thead>
                                        <tr className="text-left text-[10px] font-extrabold uppercase text-slate-400 border-b border-slate-100" style={{ letterSpacing: '1.5px', background: 'rgb(248 250 252 / 60%)' }}>
                                            <th className="px-3 py-2.5 whitespace-nowrap" style={{ minWidth: 200 }}>材料</th>
                                            {/* 列头只留两个字: 「推测、非实拍」和"未还原显示无"已经写在卡片头的口径说明里,
                                                列头再来一行副标题就是同一句话说两遍, 还把两列撑开、看不出是一组对比。
                                                原来这里的「仅已还原有」按钮同理去掉 —— 只看已还原的走筛选区那个「耗材还原」分段。 */}
                                            <th className="px-3 py-2.5 whitespace-nowrap normal-case"
                                                title="「加工前」= 耗材还原图: 视觉模型从结果图反推出的加工前耗材原样(推测, 非实拍)。只有「已还原」的记录有这张图, 其余显示「无」。">
                                                加工前
                                            </th>
                                            <th className="px-3 py-2.5 whitespace-nowrap" title="用户上报的加工结果图">加工后</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">来源</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">设备</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">加工模式</th>
                                            <th className="px-3 py-2.5 pr-6 text-right whitespace-nowrap">元素</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">反馈</th>
                                            <th className="px-3 py-2.5 whitespace-nowrap">用户</th>
                                            <th className="px-3 py-2.5 text-right whitespace-nowrap">
                                                <SortTimeHeader label="时间" order={order} onToggle={toggleOrder} />
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((r) => (
                                            <tr key={r.id} className="border-b border-slate-50 cursor-pointer hover:bg-slate-50"
                                                style={r.evaluateType === 'dislike' ? { boxShadow: 'inset 2px 0 0 #fda4af' } : undefined}
                                                onClick={() => history.push(`/assets/process-history/${r.id}`)}>
                                                <td className="px-3 py-2">
                                                    <div className="font-medium text-slate-700 break-words max-w-[320px]" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                        {r.materialName || <span className="text-slate-400">未命名</span>}
                                                    </div>
                                                    <div className="text-[11px] text-slate-400 font-mono">{r.materialXcode || r.materialIdRaw || '—'}</div>
                                                </td>
                                                {/* 加工前 / 加工后 各占一列, 与「加工数据中心」列表同款 (ProcessThumb 负责缺图占位) */}
                                                <td className="px-3 py-2">
                                                    <ProcessThumb src={r.restoredImageUrl} label="加工前 · AI 还原(推测)" onZoom={setZoomSrc} />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <ProcessThumb src={r.imageUrl} label="加工后" onZoom={setZoomSrc} />
                                                </td>
                                                <td className="px-3 py-2"><MaterialSourceBadge source={r.materialSource} /></td>
                                                <td className="px-3 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">{r.deviceName || r.deviceIdRaw}</td>
                                                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{modeText(r.mode)}</td>
                                                <td className="px-3 py-2 pr-6 text-right font-mono tabular-nums text-slate-600">{r.elementCount}</td>
                                                <td className="px-3 py-2"><EvaluateBadge type={r.evaluateType} /></td>
                                                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.userUid || '—'}</td>
                                                <td className="px-3 py-2 text-right text-[11px] font-mono text-slate-500 whitespace-nowrap">{fmtOccurred(r.occurredAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {totalPages > 1 && (
                            <div className="px-4 py-3 border-t border-slate-100 flex justify-end">
                                <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} />
                            </div>
                        )}
                    </div>
                </>
            )}
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
}


export default ProcessHistoryList;
